import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { T, F, th as palette } from "@/lib/bricks";
import { Section, Stats, List, Row, Empty, TextLink } from "@/components/calm/Calm";
import BricksLoader from "@/components/BricksLoader";
import DeskBoard from "@/components/desk/DeskBoard";

/**
 * Solo desk home for Clay/Keystone/Marble/Diamond.
 * Product lock: software is feature access only — media is always billed
 * separately at rack. Never show a shoot-credit pool or "overage" framing.
 */
export default function SoloOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  useEffect(() => {
    api.get("/dashboard/overview")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:240 }}>
      <BricksLoader variant="assemble" size={40} color={T.brick} />
    </div>
  );
  if (!data) return <DeskBoard />;

  const planPrice = data.plan_price ?? 0;
  const planName = data.plan_name || 'Clay';
  const planBlurb = data.plan_description
    || 'Media billed per shoot.';
  const openFiles = data.active_jobs || 0;
  const totalFiles = data.total_jobs || 0;
  const delivered = data.completed_jobs || data.closed_files || 0;

  const recent = (data.recent_deliveries || []).slice(0, 6);
  const shots = recent.filter(d => d?.thumbnail_url);
  const closedFiles = data.closed_files || data.completed_jobs || 0;

  return (
    <div data-testid="solo-overview-page" style={{ display:'flex', flexDirection:'column', gap:32, fontFamily:F.display, color:p.ink, paddingBottom:96 }}>

      <DeskBoard />

      <Section title={`${planName} plan`} note={planBlurb}
        action={<TextLink onClick={() => navigate('/solo/billing')}>Billing</TextLink>}>
        <Stats testid="solo-plan-stats" items={[
          { label: 'Open listings', value: String(openFiles) },
          { label: 'Delivered', value: String(delivered) },
          { label: planPrice === 0 ? 'Software, free plan' : 'Software, per month', value: `$${(planPrice || 0).toLocaleString()}` },
        ]} />
        {totalFiles > 0 && (
          <div style={{ fontSize: 12.5, color: p.ink3, marginTop: 12 }}>{totalFiles} listings in total.</div>
        )}
      </Section>

      <Section title="Recent deliveries" action={<TextLink onClick={() => navigate('/solo/properties')}>Media library</TextLink>}>
        {recent.length === 0 ? <Empty>No deliveries yet. Book a shoot and the media lands here.</Empty>
          : shots.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: 8 }}>
              {shots.slice(0, 4).map((item, i) => (
                <button key={i} type="button" onClick={() => navigate('/solo/properties')} aria-label={item.address || 'Delivery'} style={{ padding:0, border:'none', background:'none', cursor:'pointer' }}>
                  <img src={item.thumbnail_url} alt="" style={{ width:'100%', aspectRatio:'4 / 3', objectFit:'cover', borderRadius:2, display:'block' }}/>
                </button>
              ))}
            </div>
          ) : (
            <List testid="solo-recent">
              {recent.map((item, i) => (
                <Row key={i} wide={false} onClick={() => navigate('/solo/properties')} title={item.address || 'Listing'}
                  meta={[item.package, item.agent].filter(Boolean)} status="Delivered" />
              ))}
            </List>
          )}
        {closedFiles > 0 && (
          <div style={{ marginTop: 12, fontSize: 12.5 }}>
            <TextLink onClick={() => navigate('/solo/properties?filter=closed')}>{closedFiles} closed listing{closedFiles !== 1 ? 's' : ''}, still live</TextLink>
          </div>
        )}
      </Section>
    </div>
  );
}
